import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isToday, setHours, setMinutes } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { useI18n } from '../i18n/context';
import { getDateFnsLocale } from '../lib/localeRuntime';

const MAIN_START_HOUR = 9;
const MAIN_END_HOUR = 21;
const EARLY_START_HOUR = 0;
const LATE_END_HOUR = 24;
const ROW_HEIGHT = 32;
const ClassicWeekPlannerCompact = ({ workouts, balancesByClientId = {}, onWorkoutClick, onAddWorkout }) => {
  const { t, locale } = useI18n();
  const dfLocale = getDateFnsLocale(locale);
  const dayAbbrevs = useMemo(
    () => [t('planner.mon'), t('planner.tue'), t('planner.wed'), t('planner.thu'), t('planner.fri'), t('planner.sat'), t('planner.sun')],
    [t, locale]
  );

  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [showEarlyHours, setShowEarlyHours] = useState(false);
  const [showLateHours, setShowLateHours] = useState(false);

  const startHour = showEarlyHours ? EARLY_START_HOUR : MAIN_START_HOUR;
  const endHour = showLateHours ? LATE_END_HOUR : MAIN_END_HOUR;
  const totalHours = endHour - startHour;
  const totalMinutes = totalHours * 60;

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [currentWeek]);

  const timeSlots = useMemo(() => {
    return Array.from({ length: totalHours }, (_, i) => {
      const h = startHour + i;
      return { hour: h, minute: 0, label: `${h.toString().padStart(2, '0')}:00` };
    });
  }, [startHour, totalHours]);

  const getWorkoutsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayWorkouts = workouts.filter(
      (w) => w.date === dateStr && w.status !== 'cancelled' && w.status !== 'transferred'
    );
    const grouped = {};
    dayWorkouts.forEach((workout) => {
      const key = `${workout.time}_${workout.end_time || workout.time}`;
      if (!grouped[key]) {
        if (workout.isGroup && workout.groupClients?.length > 0) {
          grouped[key] = {
            ...workout,
            allClients: workout.groupClients.map((c) => ({
              id: c.id,
              nickname: c.nickname,
              name: c.name,
              avatar: c.avatar,
            })),
          };
        } else {
          grouped[key] = {
            ...workout,
            allClients: workout.client_id
              ? [{ id: workout.client_id, nickname: workout.client_nickname, name: workout.client_name, avatar: workout.client_avatar }]
              : [],
          };
        }
      } else {
        if (workout.client_id && !grouped[key].allClients.find((c) => c.id === workout.client_id)) {
          grouped[key].allClients.push({
            id: workout.client_id,
            nickname: workout.client_nickname,
            name: workout.client_name,
            avatar: workout.client_avatar,
          });
        }
        if (workout.isGroup && workout.groupClients?.length > 0) {
          workout.groupClients.forEach((client) => {
            if (!grouped[key].allClients.find((c) => c.id === client.id)) {
              grouped[key].allClients.push({
                id: client.id,
                nickname: client.nickname,
                name: client.name,
                avatar: client.avatar,
              });
            }
          });
        }
      }
    });
    return Object.values(grouped);
  };

  const getWorkoutDisplayName = (workout) => {
    const allClients = workout.allClients || [];
    if (allClients.length > 1) {
      return allClients
        .map((c) => c.name || c.nickname || '')
        .filter(Boolean)
        .join(', ');
    }
    if (workout.client_nickname === 'Самотрен') return t('trainer.selfTraining');
    return workout.client_name || allClients[0]?.name || allClients[0]?.nickname || t('trainer.clientFallback');
  };

  const getWorkoutStatus = (workout) => {
    if (!workout?.date || !workout?.time) return 'past';
    if (workout.status === 'skipped') return 'skipped';
    const [y, m, d] = workout.date.split('-').map(Number);
    const [h, min] = workout.time.split(':').map(Number);
    const dt = new Date(y, m - 1, d, h, min || 0, 0, 0);
    const now = new Date();
    if (workout.status === 'completed') return 'completed';
    if (workout.status === 'in_progress') return 'in_progress';
    if (dt < now) return 'past';
    return 'scheduled';
  };

  const getWorkoutForSlot = (dayWorkouts, slotHour) => {
    const slotStart = slotHour * 60;
    const slotEnd = (slotHour + 1) * 60;
    for (const w of dayWorkouts) {
      const [h, m] = w.time.split(':').map(Number);
      const [eh, em] = (w.end_time || w.time).split(':').map(Number);
      const workoutStart = h * 60 + m;
      let workoutEnd = eh * 60 + em;
      if (workoutEnd <= workoutStart) workoutEnd += 24 * 60;
      if (workoutStart < slotEnd && workoutEnd > slotStart) return w;
    }
    return null;
  };

  const getCellBgClass = (workout) => {
    if (!workout) return '';
    const status = getWorkoutStatus(workout);
    switch (status) {
      case 'completed': return 'bg-emerald-50/80';
      case 'in_progress': return 'bg-green-100/90';
      case 'scheduled': return 'bg-blue-50/80';
      case 'skipped': return 'bg-red-50/80';
      default: return 'bg-gray-50/80';
    }
  };

  const getWorkoutBlockBgClass = (workout) => {
    if (!workout) return 'bg-background';
    const status = getWorkoutStatus(workout);
    switch (status) {
      case 'completed': return 'bg-emerald-100 border-emerald-200';
      case 'in_progress': return 'bg-green-100 border-green-200';
      case 'scheduled': return 'bg-blue-100 border-blue-200';
      case 'skipped': return 'bg-red-100 border-red-200';
      default: return 'bg-gray-100 border-gray-200';
    }
  };

  const handleSlotClick = (date, hour, minute) => {
    if (onAddWorkout) {
      const d = setMinutes(setHours(new Date(date), hour), minute);
      onAddWorkout(d);
    }
  };

  const handleWorkoutClick = (workout, e) => {
    e?.stopPropagation();
    if (onWorkoutClick) onWorkoutClick(workout);
  };

  const navigateWeek = (direction) => {
    setCurrentWeek((prev) => (direction === 1 ? addWeeks(prev, 1) : subWeeks(prev, 1)));
  };

  const now = new Date();
  const isCurrentWeek = weekDays.some((d) => isToday(d));
  const currentTimePct =
    isCurrentWeek &&
    now.getHours() >= startHour &&
    now.getHours() < endHour
      ? ((now.getHours() - startHour) * 60 + now.getMinutes()) / totalMinutes * 100
      : null;

  return (
    <div
      className="flex flex-col gap-2 min-h-0 flex-1"
      style={{ height: 'calc(100vh - 220px)', minHeight: 280 }}
    >
      {/* Навигация */}
      <div className="flex items-center justify-between px-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateWeek(-1)}
          className="h-9 w-9 touch-manipulation"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-sm font-semibold text-center">
          {weekDays[0] && weekDays[6]
            ? `${format(weekDays[0], 'd MMM', { locale: dfLocale })} – ${format(weekDays[6], 'd MMM yyyy', { locale: dfLocale })}`
            : t('planner.weekFallback')}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateWeek(1)}
          className="h-9 w-9 touch-manipulation"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Сетка: время слева + 7 дней, скролл по вертикали */}
      <div className="relative flex-1 min-h-0 overflow-hidden rounded-lg border border-border/60 bg-background">
        <div className="overflow-auto h-full min-h-[200px]" style={{ maxHeight: 'calc(100vh - 220px)' }}>
          <div className="flex flex-col min-w-[320px] relative">
            {/* Строка заголовков: время + дни недели — закреплена при скролле */}
            <div className="flex flex-shrink-0 border-b border-border/30 sticky top-0 z-30 bg-background">
              <div className="w-11 flex-shrink-0 border-r border-border/30" />
              {weekDays.map((date, dayIndex) => {
                const isCurrentDay = isToday(date);
                return (
                  <div
                    key={dayIndex}
                    className={cn(
                      'flex-1 min-w-0 flex flex-col items-center justify-center border-r border-border/30 py-2 last:border-r-0',
                      isCurrentDay
                        ? 'bg-primary/15 text-primary font-semibold'
                        : 'bg-muted/30 text-foreground'
                    )}
                    style={{ minWidth: 32 }}
                  >
                    <span className="text-[9px] uppercase font-medium leading-tight">{dayAbbrevs[dayIndex]}</span>
                    <span className="text-xs font-semibold leading-tight">{format(date, 'd')}</span>
                  </div>
                );
              })}
            </div>

            {/* Ряд «раньше»: стрелка вверх — показать, стрелка вниз — скрыть */}
            <div className="flex flex-shrink-0 border-b border-border/15">
              <div className="w-11 flex-shrink-0 border-r border-border/30" />
              <button
                type="button"
                onClick={() => setShowEarlyHours((v) => !v)}
                className="flex-1 flex items-center justify-center py-1.5 text-muted-foreground hover:bg-muted/50 transition-colors"
                title={showEarlyHours ? t('planner.hideEarlier') : t('planner.showEarlier')}
              >
                {showEarlyHours ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </button>
            </div>

            <div className="flex relative" style={{ minHeight: totalHours * ROW_HEIGHT }}>
              {/* Индикатор текущего времени — скроллится вместе с сеткой */}
              {currentTimePct !== null && (
                <div
                  className="absolute left-11 right-0 pointer-events-none z-20 flex items-center"
                  style={{
                    top: (currentTimePct / 100) * totalHours * ROW_HEIGHT,
                  }}
                >
                  <div className="w-2.5 h-2.5 rounded-full bg-primary flex-shrink-0 -ml-1" />
                  <div className="flex-1 h-0.5 bg-primary/70" />
                </div>
              )}
              {/* Колонка времени */}
              <div className="flex-shrink-0 w-11 border-r border-border/30 sticky left-0 bg-background z-10 flex flex-col">
                <div className="flex flex-col">
                  {timeSlots.map((slot, i) => (
                    <div
                      key={i}
                      className="text-[10px] text-muted-foreground text-right pr-1.5 flex items-center justify-end bg-background border-b border-border/15"
                      style={{ height: ROW_HEIGHT }}
                    >
                      {slot.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* 7 колонок дней */}
            <div className="flex flex-1">
              {weekDays.map((date, dayIndex) => {
                const dayWorkouts = getWorkoutsForDate(date);
                const isCurrentDay = isToday(date);

                return (
                  <div
                    key={dayIndex}
                    className={cn(
                      'flex-1 min-w-0 relative flex flex-col border-r border-border/30 last:border-r-0',
                      isCurrentDay && 'bg-primary/[0.08]'
                    )}
                    style={{ minWidth: 32 }}
                  >
                    {/* Область слотов */}
                    <div className="relative" style={{ height: totalHours * ROW_HEIGHT }}>
                      {/* Сетка слотов — кликабельные ячейки с заливкой по статусу */}
                      {timeSlots.map((slot, slotIndex) => {
                        const slotWorkout = getWorkoutForSlot(dayWorkouts, slot.hour);
                        const cellBg = getCellBgClass(slotWorkout);
                        return (
                          <div
                            key={slotIndex}
                            className={`cursor-pointer hover:bg-muted/40 transition-colors border-b border-border/15 ${cellBg}`}
                            style={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              top: slotIndex * ROW_HEIGHT,
                              height: ROW_HEIGHT,
                            }}
                            onClick={() => handleSlotClick(date, slot.hour, slot.minute)}
                          />
                        );
                      })}

                      {/* Блоки тренировок */}
                      {dayWorkouts.map((workout, wi) => {
                        const [h, m] = workout.time.split(':').map(Number);
                        const startMinutes = (h - startHour) * 60 + m;
                        const topPx = (startMinutes / 60) * ROW_HEIGHT;
                        let durationMinutes = 60;
                        if (workout.end_time) {
                          const [eh, em] = workout.end_time.split(':').map(Number);
                          durationMinutes = (eh - h) * 60 + (em - m);
                          if (durationMinutes <= 0) durationMinutes += 24 * 60;
                        }
                        const heightPx = Math.max(24, (durationMinutes / 60) * ROW_HEIGHT - 2);
                        const blockBg = getWorkoutBlockBgClass(workout);

                        return (
                          <div
                            key={wi}
                            className={`absolute left-0.5 right-0.5 rounded cursor-pointer overflow-hidden flex flex-col justify-center px-1 py-0.5 border shadow-sm hover:shadow transition-shadow min-w-0 ${blockBg}`}
                            style={{
                              top: topPx + 1,
                              height: heightPx - 2,
                              zIndex: 10,
                            }}
                            onClick={(e) => handleWorkoutClick(workout, e)}
                          >
                            <span className="text-[9px] font-medium text-muted-foreground leading-tight">
                              {workout.time}
                            </span>
                            <span className="text-[10px] font-medium text-foreground truncate leading-tight">
                              {getWorkoutDisplayName(workout)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

            {/* Ряд «позже»: стрелка вниз — показать, стрелка вверх — скрыть */}
            <div className="flex flex-shrink-0 border-t border-border/15">
              <div className="w-11 flex-shrink-0 border-r border-border/30" />
              <button
                type="button"
                onClick={() => setShowLateHours((v) => !v)}
                className="flex-1 flex items-center justify-center py-1.5 text-muted-foreground hover:bg-muted/50 transition-colors"
                title={showLateHours ? t('planner.hideLater') : t('planner.showLater')}
              >
                {showLateHours ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassicWeekPlannerCompact;
